// AIコンサルティング・開発ページ（/cases）の文言と構成。
// 出典: 営業_AIコンサルティング・開発のご紹介 v0.1（2026年10月）。コメントの p.N は資料のページ。
// 資料にない事実・数字・顧客名を足さない。顧客名は資料の匿名表記のまま使う。
// LPには要点だけを置き、詳しい進め方・体制・事例の中身は資料に任せる（2026-10-17 代表指示）。
// 見出しは行の配列。長い行は、折り返してよい区切り（文節）ごとの配列にする。

// p.3
export const intro = {
  label: 'AI Consulting & Development',
  ghost: 'CONSULTING',
  title: [['課題の特定から、'], ['実装・定着まで。']],
  lead: 'どこに、どうAIを入れるかの見立てから、現場で使い続けられる形になるまで進めます。',
};

// p.4。タブで切り替える4つの段階。figure は段階ごとの小さな図。
export const services = {
  label: 'What we do',
  ghost: 'APPROACH',
  heading: ['4つの段階を、', '一気通貫で支援します'],
  steps: [
    {
      key: 'discover',
      no: '01',
      title: '課題の特定',
      line: '何に取り組むかを、最初に決めます。',
      figure: { type: 'flow', from: ['ヒアリング', '業務の棚卸し', '定量分析'], to: ['解くべき課題'] },
    },
    {
      key: 'validate',
      no: '02',
      title: '構想・検証',
      line: '本格的に作る前に、確かめます。',
      figure: { type: 'flow', from: ['試作', 'データ検証'], to: ['実現できるか', '価値があるか'], check: true },
    },
    {
      key: 'build',
      no: '03',
      title: '開発・導入',
      line: 'AIとデータを、現場の仕事に組み込みます。',
      figure: {
        type: 'grid',
        groups: [
          { title: 'AIソリューション', items: ['構造化', '創出', '意思決定', 'ワークフロー'] },
          { title: 'データサイエンス', items: ['データ基盤', '予測', '最適化', '検知・予測'] },
        ],
      },
    },
    {
      key: 'operate',
      no: '04',
      title: '運用・定着',
      line: '使われ続ける形にします。',
      figure: { type: 'cycle', items: ['効果を測る', '品質と費用を改善する', '利用を広げる'] },
    },
  ],
};

// p.7-8。料金・契約の条件は資料で案内する（LPに有償の料金表を出さない）。
export const fde = {
  label: 'FDE Team',
  ghost: 'FDE TEAM',
  heading: [['FDEチームが', '現場に入り、'], ['伺ったその場で', '作ります']],
  steps: [
    { no: '01', label: '2名1組で', items: ['業務コンサルタント', 'AIエンジニア'] },
    { no: '02', label: '週2日、現場に常駐', items: ['困りごとを伺う', 'その場で作る', '見せて直す'] },
    {
      no: '03',
      label: '1か月で貴社に残る',
      items: ['業務で動く仕組み（複数）', '作り方（型・手順書・研修）'],
      emphasis: true,
    },
  ],
};

// p.10-13。tone はマス目と凡例の塗り分け:
// solid = 作った / tint = 作り方を助言した / empty = 対応しなかった・個別には作らない
export const casesSection = {
  label: 'Cases',
  ghost: 'CASES',
  heading: ['3つの事例'],
  lead: '現場から入った例、全体から入った例、開発の例です。',
};

export const cases = [
  // p.10-11
  {
    id: 'case-1',
    no: '01',
    entry: '現場から',
    tab: '大手総合商社',
    client: '大手総合商社の営業部門',
    meta: ['約70名', '2026年7月の1か月、週2日の常駐'],
    built: { label: 'その場で作ったもの', text: '面談メモの作成、帳票の転記、可視化の画面など' },
    headline: { label: '1か月で対応したご相談', value: '40', unit: '件', note: 'ご相談64件のうち' },
    figure: {
      type: 'units',
      columns: 16,
      segments: [
        { tone: 'solid', count: 11, label: 'その場で作った' },
        { tone: 'tint', count: 29, label: '作り方を助言した' },
        { tone: 'empty', count: 24, label: '対応しなかった' },
      ],
    },
  },
  // p.12
  {
    id: 'case-2',
    no: '02',
    entry: '全体から',
    tab: '大手学習塾',
    client: '大手学習塾',
    meta: ['ファンドの投資先', '2025年12月からの4か月、3部門の26業務を調査'],
    built: { label: '作って確かめたもの', text: 'レポートの添削と、シフト・時間割の作成の仕組み' },
    // 5千万円は特定したテーマの初期投資。成果の数字ではない
    headline: {
      label: '特定したテーマの削減見込み',
      prefix: '年間',
      value: '1',
      unit: '億円以上',
      note: '初期投資は5千万円',
    },
    figure: {
      type: 'units',
      columns: 13,
      segments: [
        { tone: 'solid', count: 2, label: '作って確かめた' },
        { tone: 'empty', count: 24, label: '個別には作らない' },
      ],
    },
  },
  // p.13
  {
    id: 'case-3',
    no: '03',
    entry: '開発',
    tab: 'Webサービスの刷新',
    client: '登録ユーザー1万人超のWebサービスの運営会社',
    meta: ['Boostグループの案件'],
    built: { label: '支援の範囲', text: '仕様の復元から、再構築・保守まで' },
    headline: { label: '開発の工数', value: '75', unit: '%減', note: '従来の進め方の試算と比べて' },
    figure: {
      type: 'bars',
      metrics: [
        {
          title: '工数',
          rows: [
            { label: '従来の進め方（試算）', value: 80, unit: '人月' },
            { label: 'AI駆動開発（実績）', value: 20, unit: '人月', accent: true },
          ],
        },
        {
          title: '期間',
          rows: [
            { label: '従来の進め方（試算）', value: 13, unit: 'か月' },
            { label: 'AI駆動開発（実績）', value: 4, unit: 'か月', accent: true },
          ],
        },
      ],
    },
  },
];

// p.14。Boostグループとしての主なご支援先（守秘義務のため社名は非公開）
export const clients = {
  label: '主なご支援先（Boostグループとして）',
  items: ['大手消費財メーカー', '大手電機メーカー', '大手鉄道会社', '大手総合商社', '大手学習塾'],
};

// p.16
export const start = {
  label: 'Getting Started',
  ghost: 'START',
  heading: ['まず1時間、', 'お話を伺うところから', '始めます'],
  free: '初回のご相談は無料です',
  steps: [
    { no: '01', title: 'ご相談', desc: '現状と、実現したいことを伺います' },
    { no: '02', title: '困りごとを伺う', desc: '現場の方に伺い、着手の順番を決めます' },
    { no: '03', title: 'ご提案', desc: '体制・期間・お見積もりをお出しします' },
    { no: '04', title: '開始', desc: '小さく始め、1か月ごとに続ける範囲を決めます' },
  ],
};

// 資料請求（フォームは製品ごとの資料請求ページ /documents/consulting。src/data/documentRequests.js が interests を使う）
export const request = {
  label: 'Request',
  ghost: 'DOCUMENTS',
  heading: [['進め方・体制・事例の', '詳しい内容は、'], ['資料で', 'ご案内しています']],
  interests: [
    'FDEチームの常駐',
    '業務の棚卸し・全社のAI活用',
    'AI・エージェント開発',
    'データサイエンス',
    'まずは相談したい',
  ],
};

// /cases/:id で開ける場所。今のセクションの id と、以前のページ・セクションの id を受ける。
// 事例の id（case-1 など）は「事例」のセクションへ送り、その事例のタブを開く。
const SECTION_IDS = ['services', 'fde', 'cases', 'start', 'request'];

const ALIASES = {
  // 以前のこのページのセクション
  approach: 'services',
  'field-top': 'services',
  'fde-flow': 'fde',
  themes: 'cases',
  // 以前のページ（ジャンル・事例）
  strategy: 'services',
  agent: 'fde',
  ds: 'services',
  'consulting-dx': 'cases',
  'ai-interview': 'cases',
  'demand-forecasting': 'cases',
  'sales-insights': 'cases',
};

/** /cases/:id で開ける id のすべて（ビルド後のプリレンダーが、この id ごとにページを書き出す。src/lib/prerenderRoutes.js） */
export const CASE_DEEP_LINK_IDS = [...cases.map((c) => c.id), ...SECTION_IDS, ...Object.keys(ALIASES)];

/** /cases/:id の id から、送り先のセクションと開く事例を決める。知らない id は null。 */
export const resolveDeepLink = (id) => {
  if (!id) return null;
  if (cases.some((c) => c.id === id)) return { section: 'cases', caseId: id };
  if (ALIASES[id]) return { section: ALIASES[id], caseId: null };
  return SECTION_IDS.includes(id) ? { section: id, caseId: null } : null;
};
